import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Renders Google's own "Sign in with Google" button via the Identity
// Services script (loaded in public/index.html). Nothing renders if
// REACT_APP_GOOGLE_CLIENT_ID isn't set, so this is safe to ship before
// Google sign-in is configured.
export default function GoogleSignInButton() {
    const { googleAuth } = useAuth();
    const { lang } = useLang();
    const nav = useNavigate();
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID || !buttonRef.current) return;

        const handleCredentialResponse = async (response) => {
            try {
                const u = await googleAuth(response.credential);
                toast.success("OK");
                const pendingInvite = sessionStorage.getItem("vivoz_pending_invite");
                const pendingReferral = sessionStorage.getItem("vivoz_pending_referral");
                nav(pendingInvite ? `/join/${pendingInvite}` : pendingReferral ? `/r/${pendingReferral}` : u.role === "admin" ? "/admin" : "/dashboard");
            } catch (err) {
                toast.error(err.response?.data?.detail || "Google sign-in failed");
            }
        };

        let cancelled = false;
        const tryInit = () => {
            if (cancelled) return;
            if (!window.google?.accounts?.id) {
                setTimeout(tryInit, 100);
                return;
            }
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleCredentialResponse,
            });
            window.google.accounts.id.renderButton(buttonRef.current, {
                type: "standard",
                theme: "outline",
                size: "large",
                width: 320,
                locale: lang,
            });
        };
        tryInit();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    if (!GOOGLE_CLIENT_ID) return null;

    return <div ref={buttonRef} data-testid="google-signin-button" className="flex justify-center" />;
}
